import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/apiClient";
import type { DeliveryDocument, ScheduledDelivery } from "../../api/types";
import { dateText } from "../../i18n/intl";
import { openBlobInNewTab } from "../../utils/openBlobInNewTab";
import { PhotoViewer } from "../ui/PhotoViewer";
import { ReasonModal } from "../ui/ReasonModal";

/**
 * G-137 (OD-183 / OD-184) — the ticket, receipt or albarán of a delivery typed
 * by hand, as photos or PDFs. The owner and the manager of the delivery's shop
 * add and open them; the owner deletes any, a manager only one they added,
 * always with a reason (kept, and written to the Action log). Whether the
 * delivery is "missing" a document is the server's `documentState`.
 */
export function DeliveryDocumentsBlock({
  delivery,
  currentUserId,
  isOwner,
  onChanged,
}: {
  delivery: ScheduledDelivery;
  currentUserId: string;
  isOwner: boolean;
  /** Re-read the delivery so its flags follow (No document, No factura). */
  onChanged: () => Promise<void>;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<DeliveryDocument[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photo, setPhoto] = useState<{ url: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState<DeliveryDocument | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDocuments((await api.listDeliveryDocuments(delivery.id)).documents);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load the documents");
    }
  }, [delivery.id]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Re-read the delivery's flags; a failure here is not a failed upload or deletion. */
  const refreshDelivery = async () => {
    try {
      await onChanged();
    } catch {
      setError("The document was saved, but the page could not refresh. Reload to see the delivery's state.");
    }
  };

  const add = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      setDocuments((await api.addDeliveryDocument(delivery.id, file)).documents);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not add the document");
      return;
    } finally {
      setBusy(false);
    }
    await refreshDelivery();
  };

  const open = async (doc: DeliveryDocument) => {
    setError(null);
    try {
      if (doc.mimeType === "application/pdf") {
        await openBlobInNewTab(() => api.fetchDeliveryDocumentBlobUrl(delivery.id, doc.id));
      } else {
        setPhoto({ url: await api.fetchDeliveryDocumentBlobUrl(delivery.id, doc.id), name: doc.originalName });
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not open the document");
    }
  };

  const confirmDelete = async (reason: string | undefined) => {
    if (!deleting || !reason) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      setDocuments((await api.deleteDeliveryDocument(delivery.id, deleting.id, reason)).documents);
      setDeleting(null);
      setError(null);
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : "Could not delete the document");
      return;
    } finally {
      setDeleteBusy(false);
    }
    await refreshDelivery();
  };

  const canAdd = delivery.status !== "CANCELED";
  const list = documents ?? [];

  return (
    <div className={`card dlv-docs${delivery.documentState === "missing" ? " dlv-docs--missing" : ""}`}>
      <div className="dlv-docs-head">
        <h3 className="dlv-docs-title">Documents</h3>
        {canAdd ? (
          <>
            <button
              type="button"
              className="button tinted"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              {busy ? "Adding…" : "Add document"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void add(file);
              }}
            />
          </>
        ) : null}
      </div>

      {delivery.documentState === "missing" ? (
        <p className="inv-section-note">
          <span className="paper-pill paper-pill--warn">No document</span> This delivery was typed by hand and has
          arrived. Add a photo or PDF of {delivery.supplier.name}&apos;s ticket, receipt or albarán.
        </p>
      ) : delivery.documentState === "covered" ? (
        <p className="inv-section-note muted">No document needed: the factura is its paper. You can still add one.</p>
      ) : delivery.documentState === "not-arrived" ? (
        <p className="inv-section-note muted">Add the ticket, receipt or albarán when it comes with the goods.</p>
      ) : null}

      {list.length > 0 ? (
        <ul className="dlv-docs-list">
          {list.map((doc) => (
            <li key={doc.id} className="dlv-docs-row">
              <button type="button" className="button plain dlv-docs-name" onClick={() => void open(doc)}>
                {doc.originalName}
              </button>
              <span className="muted dlv-docs-meta">
                {doc.mimeType === "application/pdf" ? "PDF" : "Photo"} · added by {doc.uploadedBy.fullName} on{" "}
                {dateText(new Date(doc.uploadedAt))}
              </span>
              {isOwner || doc.uploadedBy.id === currentUserId ? (
                <button
                  type="button"
                  className="button plain dlv-docs-delete"
                  onClick={() => {
                    setDeleteError(null);
                    setDeleting(doc);
                  }}
                >
                  Delete
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {error ? (
        <div className="alert error" role="alert">
          {error}
        </div>
      ) : null}

      {photo ? (
        <PhotoViewer
          title={photo.name}
          url={photo.url}
          alt={`Document ${photo.name}`}
          onClose={() => {
            URL.revokeObjectURL(photo.url);
            setPhoto(null);
          }}
        />
      ) : null}

      {deleting ? (
        <ReasonModal
          title="Delete this document?"
          message={`"${deleting.originalName}" leaves the delivery. Who deleted it, when and why are kept in the Action log.`}
          reasonMode="required"
          reasonLabel="Why is it deleted?"
          reasonPlaceholder="e.g. wrong delivery's ticket"
          minReasonLength={3}
          confirmLabel="Delete document"
          destructive
          busy={deleteBusy}
          error={deleteError}
          onConfirm={confirmDelete}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
}
